import { StanBadge, StanButton, StanCard, StanFilters, StanTable, StanToggle } from '@/components';
import { useReferralsFilters } from '../hooks/useReferralsFilters';
import { useReferralsTable } from '../hooks/useReferralsTable';
import { configSettings } from '@/lib/config';
import { SUBSCRIPTION_STATUS } from '../constants/referrals.constants';
import { getSubscriptionStatusLabel } from '../logic';
import { createIconElement } from '@/lib/utils/createIconElement';
import CopyIconSvg from '@/assets/icons/ic-copy.svg';

const CopyIcon = createIconElement(CopyIconSvg, { className: 'size-4' });

export function ReferralsTable() {
    const filters = useReferralsFilters();
    const {
        // State
        filterValues,
        currentPage,
        excludeInactive,
        // Data
        referrals,
        metadata,
        isLoading,
        // Computed
        emptyHeader,
        emptySubheader,
        // Handlers
        handleToggleChange,
        handleFilterChange,
        handleResetFilters,
        handlePageChange,
        handleCopyLink,
        handleDownloadCSV,
    } = useReferralsTable();

    const columns = [
        {
            header: 'User',
            accessorKey: 'username',
            cell: (info: { getValue: () => string }) => (
                <span className='text-dark text-label-sm-bold'>{info.getValue()}</span>
            ),
        },
        {
            header: 'Email',
            accessorKey: 'email',
            cell: (info: { getValue: () => string }) => {
                const email = info.getValue();
                const truncatedEmail = email.length > 20 ? `${email.substring(0, 20)}...` : email;
                return <span className='text-gray text-label-sm-regular'>{truncatedEmail}</span>;
            },
        },
        {
            header: 'Sign Up Date',
            accessorKey: 'subscriberSince',
            hideOnMobile: true,
            cell: (info: { getValue: () => string }) => (
                <span className='text-gray text-label-sm-regular'>{info.getValue()}</span>
            ),
        },
        {
            header: 'Source',
            accessorKey: 'utm_source',
            hideOnMobile: true,
            cell: (info: { getValue: () => string }) => (
                <span className='text-gray text-label-sm-regular'>{info.getValue()}</span>
            ),
        },
        {
            header: 'Status',
            accessorKey: 'subscriptionStatus',
            hideOnMobile: true,
            cell: (info: { getValue: () => number }) => {
                const status = info.getValue();
                const isActive = status === SUBSCRIPTION_STATUS.ACTIVE;

                return (
                    <StanBadge variant={isActive ? 'success' : 'destructive'} size='sm'>
                        {getSubscriptionStatusLabel(status)}
                    </StanBadge>
                );
            },
        },
        {
            id: 'stanLink',
            header: 'Stan Link',
            accessorKey: 'username',
            hideOnMobile: true,
            cell: (info: { getValue: () => string }) => {
                const username = info.getValue();
                const fullLink = `${configSettings.frontDomain}${username}`;

                return (
                    <a
                        href={fullLink}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-brand-main text-label-sm-regular hover:underline'
                    >
                        {fullLink}
                    </a>
                );
            },
        },
        {
            id: 'copyButton',
            header: '',
            accessorKey: 'username',
            align: 'right' as const,
            cell: (info: { getValue: () => string }) => {
                const username = info.getValue();
                const fullLink = `${configSettings.frontDomain}${username}`;

                return (
                    <div className='flex justify-end'>
                        <StanButton
                            name='copy-referral-link'
                            color='secondary'
                            size='xs'
                            onClick={() => handleCopyLink(fullLink)}
                            icon={CopyIcon}
                        >
                            Copy
                        </StanButton>
                    </div>
                );
            },
        },
    ];

    return (
        <StanCard>
            <div className='mb-6 flex w-full items-center justify-between'>
                <h2 className='text-dark text-heading-h2'>My Referrals</h2>
                <StanButton
                    name='download-csv'
                    color='secondary'
                    size='sm'
                    onClick={handleDownloadCSV}
                    disabled={!referrals.length}
                >
                    Download CSV
                </StanButton>
            </div>

            <StanFilters
                filters={filters}
                onFilterChanged={handleFilterChange}
                onResetFilters={handleResetFilters}
                filterValues={filterValues}
                className='mb-6'
            />

            <StanTable
                columns={columns}
                data={referrals}
                isLoading={isLoading}
                page={currentPage}
                totalPages={metadata?.totalPages ?? 1}
                onPageChange={handlePageChange}
                emptyHeader={emptyHeader}
                emptySubheader={emptySubheader}
                totalRowsLabel={
                    <div className='flex w-full items-center justify-between'>
                        <div className='flex items-center gap-2'>
                            <StanToggle checked={excludeInactive} onChange={handleToggleChange} toggleSize='sm' />
                            <label className='text-dark text-label-xs-bold'>Hide Inactive Users</label>
                        </div>
                    </div>
                }
            />
        </StanCard>
    );
}
